import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const CONTENT_BLOCK_LIST_QUERY = gql`
  query ContentBlockList(
    $filter: ContentBlockFilter
    $params: PaginationAndSorting
  ) {
    contentBlockList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        name
        description
        contentType
        contentData
        fileUrl
        uploadedFileId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        blockSettings
        orderNo
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONTENT_BLOCK_GET_QUERY = gql`
  query ContentBlockGet($id: ID) {
    contentBlockGet(id: $id) {
      code
      data {
        id
        venueId
        name
        description
        contentType
        contentData
        fileUrl
        uploadedFileId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        blockSettings
        orderNo
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONTENT_BLOCK_GET_MANY_QUERY = gql`
  query ContentBlockGetMany($ids: [ID]) {
    contentBlockGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        name
        description
        contentType
        contentData
        fileUrl
        uploadedFileId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        blockSettings
        orderNo
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const CONTENT_BLOCK_CREATE_MUTATION = gql`
  mutation ContentBlockCreate($params: ContentBlockInput) {
    contentBlockCreate(params: $params) {
      code
      data {
        id
        venueId
        name
        description
        contentType
        contentData
        fileUrl
        uploadedFileId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        blockSettings
        orderNo
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONTENT_BLOCK_UPDATE_MUTATION = gql`
  mutation ContentBlockUpdate(
    $where: ContentBlockWhereInput
    $params: ContentBlockInput
  ) {
    contentBlockUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        name
        description
        contentType
        contentData
        fileUrl
        uploadedFileId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        blockSettings
        orderNo
        status
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const CONTENT_BLOCK_REMOVE_MUTATION = gql`
  mutation ContentBlockRemove($where: ContentBlockWhereInput) {
    contentBlockRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface ContentBlock {
  id: string;
  venueId?: string;
  name?: string;
  description?: string;
  contentType?: string;
  contentData?: string;
  fileUrl?: string;
  uploadedFileId?: string;
  durationSeconds?: number;
  startDatetime?: string;
  endDatetime?: string;
  startTime?: string;
  endTime?: string;
  recurrencePattern?: string;
  recurrenceDays?: string;
  blockSettings?: string;
  orderNo?: number;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ContentBlockFilter {
  id?: string[];
  venueId?: string[];
  name?: string;
  searchKeyword?: string;
  contentType?: string[];
  uploadedFileId?: string;
  status?: number[];
}

export interface ContentBlockInput {
  venueId?: string | null | undefined;
  name?: string;
  description?: string | null | undefined;
  contentType?: string | null | undefined;
  contentData?: string | null | undefined;
  fileUrl?: string | null | undefined;
  uploadedFileId?: string | null | undefined;
  durationSeconds?: number | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  recurrencePattern?: string | null | undefined;
  recurrenceDays?: string | null | undefined;
  blockSettings?: string | null | undefined;
  orderNo?: number;
  status?: number;
}

export interface ContentBlockWhereInput {
  id: string;
}

/**
 * Content Block service that handles all content block-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class ContentBlockService {
  /**
   * Get a list of content blocks with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: ContentBlockFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONTENT_BLOCK_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single content block by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONTENT_BLOCK_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple content blocks by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(CONTENT_BLOCK_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new content block
   */
  static async create(params: ContentBlockInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      CONTENT_BLOCK_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing content block
   */
  static async update(
    id: string,
    params: ContentBlockInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      CONTENT_BLOCK_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a content block
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      CONTENT_BLOCK_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }
}
